import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Grid, GridSearchItem, ServiceSearchItem} from '../../../../shared/Models/grid';
import {ActionColumn, ActionItem, TextColumn} from '../../../../shared/Models/columns';
import {Router} from '@angular/router';
import {OrderService} from '../../../Services/order.service';
import * as moment from 'moment';
import {environment} from '../../../../../environments/environment';
import {Order} from '../../../../back-office/Models/Order';
import {TranslateService} from '@ngx-translate/core';
import {BroadcastService} from '../../../../shared/Services/broadcast.service';
import {Helper} from '../../../../helpers/helper.service';
import {AuthenticationService} from '../../../../shared/Services/authentication.service';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {NgbCountryDateParserFormatter} from '../../../../shared/Services/NgbCountryDateParserFormatter';
import * as _ from 'lodash';
import {CollectionService} from '../../../../back-office/Services/collection.service';
import {IButton} from '../../../../shared/card/card.component';

@Component({
  selector: 'app-quote-overview',
  templateUrl: './quote-overview.component.html',
  styleUrls: ['./quote-overview.component.scss'],
  providers: [
    {provide: NgbDateParserFormatter, useClass: NgbCountryDateParserFormatter}
  ],
})
export class QuoteOverviewComponent implements OnInit {
  public grid: Grid;
  public params;
  public orderReference: string;
  public orderId: string;
  @ViewChild('expectedDeliveryWeekTemp', {static: true}) expectedDeliveryWeekTemp: ElementRef;
  @ViewChild('cancelModal', {static: false}) cancelModal = null;
  @ViewChild('customerPriceTemp', {static: true}) customerPriceTemp;
  @ViewChild('remainingTimeTemp', {static: true}) remainingTimeTemp;
  public baseUrl = environment.apiUrl;
  public advancedButtons: IButton[] = [];
  public addButton;

  constructor(
    private orderService: OrderService,
    private router: Router,
    private broadcastService: BroadcastService,
    private translator: TranslateService,
    private helper: Helper,
    private auth: AuthenticationService,
    private collectionService: CollectionService) {
  }

  ngOnInit() {
    this.addButton = {
      key: 'sync',
      btnName: this.translator.instant('global.new_quote'),
      method: 'newQuote',
      btnCustomClass: 'btn-primary',
      btnIcon: 'feather icon-plus'
    };

    this.advancedButtons.push(this.addButton);
    const dateFormat = this.auth.getDateFormat();
    this.grid = <Grid>{
      columns: [
        <TextColumn>{
          name: 'doctrine.quote.number',
          prop: 'orderNo',
          sortable: true,
          type: TextColumn.type,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.date',
          prop: 'created',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          valueFormatter: row => moment(row).format(dateFormat),
        },
        <TextColumn>{
          name: 'doctrine.quote.reference',
          prop: 'reference',
          sortable: true,
          type: TextColumn.type,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.total_costs',
          prop: 'totalCost.totalCustomerCost',
          sortable: true,
          valueTemplate: this.customerPriceTemp,
          translate: true,
        },
        <TextColumn>{
          name: 'doctrine.quote.remaining_time',
          prop: 'remainingDays',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          valueTemplate: this.remainingTimeTemp,
        },
        <ActionColumn>{
          name: 'global.actions',
          prop: null,
          type: ActionColumn.type,
          sortable: false,
          translate: true,
          actionItems: [
            <ActionItem>{
              name: 'global.view',
              icon: '',
              status: true,
              tooltip: 'global.view',
              onclick: row => this.router.navigateByUrl('dealer-system/quote/' + row.id + '/show'),
              showCallback: row => row && row.remainingDays > 0,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              name: 'global.view_expired',
              icon: '',
              status: true,
              tooltip: 'global.view_expired',
              onclick: row => this.router.navigateByUrl('dealer-system/quote/' + row.id + '/show'),
              showCallback: row => row && row.remainingDays <= 0,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              name: 'global.edit',
              icon: '',
              status: true,
              tooltip: 'global.edit',
              onclick: row => this.router.navigateByUrl('dealer-system/quote/' + row.id + '/edit'),
              showCallback: (row: Order) => !this.auth.isDealerTabletUser() && this.isAllowedToEdit(row),
              btnClass: 'ripple light ms-1'
            },
            <ActionItem>{
              name: 'global.cancel',
              icon: '',
              status: true,
              tooltip: 'global.cancel',
              onclick: row => {
                this.orderReference = row.reference;
                this.orderId = row.id;
                this.cancelModal.show();
              },
              showCallback: (row: Order) => row,
              btnClass: 'ripple light ms-1'
            },
          ]
        }

      ],
      service: this.orderService,
      rememberURL: this.router.url,
      method: 'getQuotes',
      root: 'order',
      defaultSort: 'id',
      rowDetailShowCallback: (row, size) => size < 1400,
      defaultSortType: 'desc',
      showButton: true,
      doseNotHaveSearch: true,
      search: !this.auth.isDealerTabletUser() ? [
        <GridSearchItem>{
          calendarFromDate: 'from',
          calendarToDate: 'to',
          hasCalendar: true,
          calendarCustomWidth: 39,
          label: 'doctrine.attributes.model',
          hasDropdownListFilter: false,
          hasStatus: false,
          hasNormalSearch: false,
        }
      ] : [
        <GridSearchItem>{
          hasCalendar: false,
          label: 'doctrine.attributes.model',
          hasDropdownListFilter: false,
          hasStatus: false,
          hasNormalSearch: false,
        }
      ] ,
    };

  }

  setParams(event) {
    this.params = event;
  }

  cancelQuote() {
    this.orderService.cancelQuoteOrder(this.orderId).subscribe((resp) => {
      this.helper.popMessageDialog(
        this.translator.instant('global.success_message.title'),
        null,
        'success',
        false
      );
      this.broadcastService.broadcast('refreshGrid', 'getQuotes');
      this.cancelModal.hide();
    });
  }

  isAllowedToEdit(row) {
    const created = moment(row.created).add(Order.DAYS_ALLOWED_FOR_EDITING, 'days');
    const now = moment();
    const isOrderNewOrInProcess = (row.status === Order.NEW.key || row.status === Order.IN_PROCESS.key);
    const isNotPastFromAllowedDays = created.isAfter(now);

    if (isOrderNewOrInProcess && isNotPastFromAllowedDays) {
      return row;
    }

    return;
  }

  newQuote() {
    return this.router.navigateByUrl('dealer-system/order/add');
  }

  calFunction(method: string) {
    this[method]();
  }
}
